import { DSErrorDetail } from '@digitaservice/component-types';
import { captureException } from '@sentry/angular';

/**
 * Longest normalized message kept for grouping. Sentry caps a tag value at 200 characters and a
 * fingerprint entry this long is already past the point of adding any discrimination.
 */
const MaxNormalizedLength = 120;

/**
 * Strips the parts of a failure message that vary per campaign, per asset and per release, so the
 * remainder describes the *kind* of failure.
 *
 * An element states what it could not load, which means the message carries a CDN URL, an asset id
 * and often a version number - all of which differ between two reports of the same bug. Grouping on
 * the raw text would file one broken loader as a separate issue for every campaign that hit it, the
 * same fan-out that makes a chunk-load issue useless. URLs go first so the hashes inside them are
 * already gone by the time the id rule runs.
 *
 * @param message - the message the element reported.
 * @returns the message with identifiers replaced by placeholders.
 */
const normalizeFailureMessage = (message: string): string =>
  message
    .replace(/(?:blob:)?https?:\/\/\S+/gi, '%URL%')
    .replace(/\b\d+\.\d+\.\d+\b/g, '%VERSION%')
    .replace(/\b[0-9a-f]{8,}\b/gi, '%ID%')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MaxNormalizedLength);

/**
 * Rebuilds the element's error as a real Error so Sentry reports the failure the element actually
 * had, rather than the frame in this component that received the news of it.
 *
 * The element sends its own `stack` across the event boundary; adopting it puts the element's
 * frames - the loader, the initializer, the game - on the Sentry event. Those frames live on
 * cdn-apps.drimify.com, which is a first-party host as far as `allowUrls` is concerned, so keeping
 * them does not get the event dropped.
 *
 * @param detail - the error detail from the element's `error` event.
 * @returns an Error carrying the element's name, message and stack.
 */
export const elementFailureError = (detail: DSErrorDetail): Error => {
  const error = new Error(detail.message);
  if (detail.name) {
    error.name = detail.name;
  }
  if (detail.stack) {
    error.stack = detail.stack;
  }
  return error;
};

/**
 * Reports a failure an element raised about itself.
 *
 * Nothing else reports these. An element that dies mid-session tells this component and no one
 * else, so its message - the only text that names the asset or the step that failed - existed
 * only in a console breadcrumb, and whatever uncaught remains the browser happened to notice
 * became the Sentry issue in its place. That issue is titled from an error nobody wrote and
 * carries no way back to the element, which is how a real failure affecting real players reads
 * as noise.
 *
 * Grouped by element tag, error name and normalized message, so each distinct way an element can
 * fail is one issue across every campaign running it. The raw message stays as the event's value
 * because that is what a title is for: it names the asset that failed.
 *
 * @param componentTag - the custom element tag, e.g. `ds-spin-the-wheel-3d`.
 * @param detail - the error detail from the element's `error` event.
 */
export const reportElementFailure = (componentTag: string, detail: DSErrorDetail) => {
  const normalized = normalizeFailureMessage(detail.message);
  captureException(elementFailureError(detail), {
    fingerprint: ['element-error', componentTag, detail.name ?? 'Error', normalized],
    tags: {
      'element.tag': componentTag,
      'element.failure': normalized,
    },
    // The element stringifies its own `cause` before sending it, so it can never be an Error for
    // linkedErrorsIntegration to follow. Carried as data instead of being dropped, because for a
    // failed asset load the cause is the only place the underlying reason appears.
    ...(detail.cause ? { extra: { cause: detail.cause } } : {}),
  });
};

/**
 * Reports an element that never started because its config or its JS bundle could not be fetched.
 *
 * Deliberately one group for the whole app. The failure is always the same one - a request for a
 * versioned URL on the asset CDN did not come back - and the element it was for is a tag on the
 * event rather than part of the fingerprint, so a bad deploy reads as one issue climbing instead
 * of a new issue per game.
 *
 * @param src - the bundle URL that was being loaded.
 * @param error - whatever the load threw.
 */
export const reportElementLoadFailure = (src: string, error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);
  captureException(error instanceof Error ? error : new Error(message), {
    fingerprint: ['element-load', normalizeFailureMessage(message)],
    tags: {
      'element.src': src.slice(0, MaxNormalizedLength),
    },
  });
};
